import * as React from 'react';
import { format, subDays, isValid, parse } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { DatePickerTrigger } from '@/components/composite/date-picker-trigger/date-picker-trigger';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const PRESETS = [
  {
    label: '今天',
    getRange: () => {
      const d = format(new Date(), 'yyyy-MM-dd');
      return { start: `${d} 00:00:00`, end: `${d} 23:59:59` };
    },
  },
  {
    label: '昨天',
    getRange: () => {
      const d = format(subDays(new Date(), 1), 'yyyy-MM-dd');
      return { start: `${d} 00:00:00`, end: `${d} 23:59:59` };
    },
  },
  {
    label: '近7天',
    getRange: () => ({
      start: `${format(subDays(new Date(), 6), 'yyyy-MM-dd')} 00:00:00`,
      end: `${format(new Date(), 'yyyy-MM-dd')} 23:59:59`,
    }),
  },
  {
    label: '近30天',
    getRange: () => ({
      start: `${format(subDays(new Date(), 29), 'yyyy-MM-dd')} 00:00:00`,
      end: `${format(new Date(), 'yyyy-MM-dd')} 23:59:59`,
    }),
  },
];

function parseDatePart(s: string): Date | undefined {
  if (!s) return undefined;
  const d = parse(s, 'yyyy-MM-dd', new Date());
  return isValid(d) ? d : undefined;
}

/** value / onChange use full "YYYY-MM-DD HH:mm:ss" strings */
function DateField({
  label,
  value,
  onChange,
  defaultTime,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  defaultTime: string;
}) {
  const [calendarOpen, setCalendarOpen] = React.useState(false);
  // Internal input state so typing character-by-character doesn't get reset
  // while the date is still incomplete. Parent state only updates on valid dates.
  const [inputValue, setInputValue] = React.useState(value.slice(0, 10));

  const timePart = value.length >= 19 ? value.slice(11, 19) : defaultTime;

  // Sync when parent changes the value externally (e.g. preset, calendar pick)
  React.useEffect(() => {
    setInputValue(value.slice(0, 10));
  }, [value]);

  const handleDateChange = (d: string) => {
    setInputValue(d);
    if (!d) {
      onChange('');
      return;
    }
    if (parseDatePart(d)) onChange(`${d} ${timePart}`);
  };

  const handleDateBlur = () => {
    if (inputValue && !parseDatePart(inputValue)) {
      setInputValue('');
      onChange('');
    }
  };

  const handleCalendarSelect = (date: Date | undefined) => {
    if (date) handleDateChange(format(date, 'yyyy-MM-dd'));
    setCalendarOpen(false);
  };

  const setTime = (t: string) => {
    if (inputValue) onChange(`${inputValue} ${t}`);
  };

  const selected = parseDatePart(inputValue);

  return (
    <div className="space-y-1.5">
      <p className="text-xs text-muted-foreground">{label}</p>

      {/* 日期行 */}
      <div className="flex items-center gap-1">
        <Input
          className="h-8 flex-1 font-mono text-sm"
          placeholder="YYYY-MM-DD"
          value={inputValue}
          onChange={(e) => handleDateChange(e.target.value)}
          onBlur={handleDateBlur}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={`选择${label}`}
          className={cn('h-8 w-8 shrink-0', calendarOpen && 'bg-accent')}
          onClick={() => setCalendarOpen((v) => !v)}
        >
          <CalendarIcon className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* 时间行 */}
      <Input
        type="time"
        step="1"
        className="h-8 font-mono text-sm"
        value={timePart}
        disabled={!inputValue}
        onChange={(e) => setTime(e.target.value)}
      />

      {/* 日历 */}
      {calendarOpen && (
        <div className="pt-1">
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected ?? new Date()}
            onSelect={handleCalendarSelect}
            className="rounded-md border p-2 shadow-sm"
          />
        </div>
      )}
    </div>
  );
}

interface DateRangePickerProps {
  label: string;
  startValue?: string;
  endValue?: string;
  onConfirm: (start: string | undefined, end: string | undefined) => void;
  onClear: () => void;
  className?: string;
}

export function DateRangePicker({
  label,
  startValue,
  endValue,
  onConfirm,
  onClear,
  className,
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [localStart, setLocalStart] = React.useState('');
  const [localEnd, setLocalEnd] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setLocalStart(startValue ?? '');
      setLocalEnd(endValue ?? '');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasValue = !!(startValue || endValue);
  const displayText = hasValue
    ? [startValue?.slice(0, 16), endValue?.slice(0, 16)].filter(Boolean).join(' ~ ')
    : undefined;

  const applyPreset = (start: string, end: string) => {
    onConfirm(start, end);
    setOpen(false);
  };

  const handleConfirm = () => {
    onConfirm(
      localStart && parseDatePart(localStart.slice(0, 10)) ? localStart : undefined,
      localEnd && parseDatePart(localEnd.slice(0, 10)) ? localEnd : undefined,
    );
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <DatePickerTrigger
          label={label}
          displayText={displayText}
          hasValue={hasValue}
          onClear={onClear}
          className={className}
        />
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        {/* 快捷预设 */}
        <div className="mb-2 flex flex-wrap gap-1">
          {PRESETS.map((p) => {
            const { start, end } = p.getRange();
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => applyPreset(start, end)}
                className="rounded-md border border-muted bg-muted/40 px-2.5 py-1 text-xs font-medium transition-colors hover:bg-muted"
              >
                {p.label}
              </button>
            );
          })}
        </div>
        <div className="mb-3 h-px bg-border" />

        {/* 日期时间字段 */}
        <div className="space-y-3">
          <DateField
            label="开始时间"
            value={localStart}
            onChange={setLocalStart}
            defaultTime="00:00:00"
          />
          <DateField
            label="结束时间"
            value={localEnd}
            onChange={setLocalEnd}
            defaultTime="23:59:59"
          />
          <Button size="sm" className="w-full" onClick={handleConfirm}>
            确认
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
